/* THE PUBLIC IDENTITY — the name and face a coach shows the community (W6, 2026-09-18).
 *
 * 🔴 **`community_identities` IS A NEW SURFACE AND NEVER A WIDENING OF `profiles`** — his decision,
 * and the reason is a security property rather than tidiness: RLS chooses ROWS and has NO
 * VOCABULARY FOR COLUMNS, so on a table readable by everyone the COLUMN LIST *is* the boundary.
 * A field added there becomes readable by every signed-in person with no policy change and nothing
 * for a review to see. `CLAUDE.md` records that as its own trap, on this exact table.
 *
 * ⚠️ **THIS FILE HOLDS THE RULES ONLY.** It writes nothing and reads nothing; the screen does.
 */

/** The database's own rule, measured rather than remembered:
 *  `CHECK (length(btrim(display_name)) >= 1 AND length(btrim(display_name)) <= 80)`
 *  — the same 80 the phone's `CommunityIdentity.nameLimit` carries. */
export const NAME_LIMIT = 80;

export type NameFault =
  | { kind: "empty" }
  | { kind: "tooLong"; length: number };

/** Refuse in the app what the database would refuse, so the failure is a sentence rather than an
 *  error code. ⚠️ **A COURTESY, NOT THE RULE — the CHECK is the rule**, and this file being wrong
 *  makes a message wrong, never a row. The phone's own comment says the same. */
export function nameFault(raw: string): NameFault | null {
  const trimmed = raw.trim();
  if (trimmed === "") return { kind: "empty" };
  if (trimmed.length > NAME_LIMIT) return { kind: "tooLong", length: trimmed.length };
  return null;
}

export function nameFaultMessage(f: NameFault): string {
  return f.kind === "empty"
    ? "Pick a name people will see."
    : `That is ${f.length} characters — the limit is ${NAME_LIMIT}.`;
}

/* 🔴 **NEITHER HALF OF A CONSENT QUESTION MAY HAVE A DEFAULT.**
 *
 * `is_visible` and `allows_messages` are both NOT NULL with **no default** (measured), so an insert
 * omitting either is refused — and that is deliberate on both sides. The phone's `Draft` keeps them
 * `Bool?` for the same reason it keeps a pain slider tri-state: *a toggle that starts in a position
 * is an answer nobody gave*, and here the answer decides whether a real person's name is shown to
 * strangers.
 *
 * ⚠️ Somebody who has ALREADY answered starts at their stored answers, which is not a default — it
 * is what they said last time, which is what a settings screen owes them.
 */
export type ConsentDraft = {
  name: string;
  visible: boolean | null;
  allowsMessages: boolean | null;
};

export type SaveState =
  | { kind: "ready"; name: string; visible: boolean; allowsMessages: boolean }
  | { kind: "unanswered" }
  | { kind: "nameFault"; fault: NameFault };

/** Whether this draft may be saved, and what it would save. */
export function saveState(d: ConsentDraft): SaveState {
  const fault = nameFault(d.name);
  if (fault !== null) return { kind: "nameFault", fault };
  // ⚠️ `null` is UNANSWERED and is not `false`. Treating it as false would publish a decision
  // nobody took — in the direction that happens to be safe today, which is exactly how a default
  // survives long enough to be relied on.
  if (d.visible === null || d.allowsMessages === null) return { kind: "unanswered" };
  return {
    kind: "ready",
    name: d.name.trim(),
    visible: d.visible,
    allowsMessages: d.allowsMessages,
  };
}

/**
 * ⚠️ **WITHDRAWAL IS `is_visible = false`, NEVER A DELETE.**
 * MEASURED: DELETE on `community_identities` is granted to NOBODY. So "take my name down" is an
 * UPDATE, and the row survives — which is what lets somebody come back without re-consenting from
 * scratch, and what stops a withdrawal orphaning the posts and comments that point at it.
 */
export function withdrawal(): { is_visible: false } {
  return { is_visible: false };
}

/** What the coach is told they are, right now. */
export function describeIdentity(row: { is_visible: boolean; allows_messages: boolean } | null): string {
  if (row === null) return "You have no public name yet.";
  if (!row.is_visible) return "Your name is hidden. Nobody in the community can see it.";
  return row.allows_messages
    ? "Your name is visible, and people can message you."
    : "Your name is visible. Messages are off.";
}

/**
 * HOW LOUDLY THE NAME FAULT SPEAKS — W13.
 *
 * 🔴 THE DEFECT. `nameFault` is true the moment the field is empty, and the field is empty the
 * moment the page opens for somebody who has never published a name. So the first thing a coach
 * saw on this screen was a `role="alert"` telling them off for not having typed yet — a
 * screen-reader announcement, in the error colour, about an omission that is not one.
 *
 * ⚠️ AND IT IS A DIFFERENT FAULT AFTER THEY HAVE TYPED. "You have not typed a name" and "what you
 * typed will not save" are two facts, and only the second has earned an interruption. So the tone
 * is a function of WHAT THE PERSON HAS DONE, not of whether the string currently passes:
 *
 *   * nothing wrong                                 → `silent`
 *   * wrong, but they have neither typed nor saved  → `hint`   (quiet, no `role`, no colour)
 *   * wrong, and they have typed or pressed Save    → `alert`  (announced, error colour)
 *
 * ⚠️ IT NEVER GOES SILENT ON A REAL FAULT. A form that hides the reason Save is unavailable is
 * worse than one that shouts it — the coach is left clicking a dead button. The hint says the
 * same sentence; it just says it in the register of an instruction rather than a complaint.
 *
 * ⚠️ AND `attempted` LATCHES. Pressing Save on an invalid form earns the alert and keeps it: a
 * tone that fell back to a hint on the next keystroke would flicker between two registers while
 * somebody is correcting the thing it is about.
 */
export type FaultTone = "silent" | "hint" | "alert";

export function faultTone(
  hasFault: boolean,
  behaviour: { typed: boolean; attempted: boolean },
): FaultTone {
  if (!hasFault) return "silent";
  return behaviour.typed || behaviour.attempted ? "alert" : "hint";
}
